import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense } from "react";
import { useLazyLoadQuery } from "react-relay";
import { Navigate, Route, Routes } from "react-router-dom";
import { InternBrandWorkspaceQuery } from "./__generated__/InternBrandWorkspaceQuery.graphql";
import { useBrandWorkspace } from "../../contexts/BrandWorkspaceContext";
import InternBrandBilling from "./InternBrandBilling";
import InternBrandBuilds from "./InternBrandBuilds";
import InternContentGenContainer from "./gen/InternContentGenContainer";
import InternBrandJobs from "./jobs/InternBrandJobs";
import InternBrandOnboarding from "./onboarding/InternBrandOnboarding";
import InternBrandDashboard from "./overview/InternBrandDashboard";
import InternBrandStyle from "./style/InternBrandStyle";

const query = graphql`
  query InternBrandWorkspaceQuery($id: ID!) {
    brand(id: $id) {
      id
      displayName
      appConfig {
        appState
      }
      ...InternBrandBilling_brand
      ...InternBrandBuilds_brand
      ...InternBrandDashboard_brand
      ...InternBrandOnboarding_brand
    }
  }
`;

const APP_STATE_CONFIG: Record<
  string,
  { label: string; color: "success" | "warning" | "error" | "default" | "info" }
> = {
  PRODUCTION: { label: "Production", color: "success" },
  PRE_PRODUCTION: { label: "Pre-Production", color: "info" },
  PENDING_KINN_SETUP: { label: "Pending Kinn Setup", color: "error" },
  PENDING_BRAND_SETUP: { label: "Pending Brand Setup", color: "warning" },
  DISABLED: { label: "Disabled", color: "default" },
};

// Content component that loads brand data
const WorkspaceContent = ({ brandId }: { brandId: string }) => {
  const data = useLazyLoadQuery<InternBrandWorkspaceQuery>(query, {
    id: brandId,
  });
  const brand = data.brand;
  const appConfig = brand?.appConfig;
  const appState = appConfig?.appState ?? "DISABLED";
  const stateConfig = APP_STATE_CONFIG[appState] ?? APP_STATE_CONFIG.DISABLED;

  if (!brand) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          color: "var(--intern-text-tertiary)",
        }}
      >
        <Typography>Brand not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Brand Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: "var(--intern-text-primary)",
                fontSize: { xs: "1rem", sm: "1.125rem" },
              }}
            >
              {brand.displayName}
            </Typography>
            <Chip
              label={stateConfig.label}
              size="small"
              color={stateConfig.color}
              variant="outlined"
              sx={{ fontWeight: 500, fontSize: "0.75rem" }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Routes */}
      <Routes>
        <Route
          path="/overview"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandDashboard brand={brand} />
            </Suspense>
          }
        />
        <Route
          path="/billing"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandBilling brand={brand} />
            </Suspense>
          }
        />
        <Route
          path="/onboarding"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandOnboarding brand={brand} />
            </Suspense>
          }
        />
        <Route
          path="/builds"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandBuilds brand={brand} />
            </Suspense>
          }
        />
        <Route
          path="/jobs"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandJobs brandId={brand.id} />
            </Suspense>
          }
        />
        <Route
          path="/style"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternBrandStyle brandID={brand.id} />
            </Suspense>
          }
        />
        <Route
          path="/generation"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InternContentGenContainer brandID={brand.id} />
            </Suspense>
          }
        />
        {/* Default redirect to overview */}
        <Route path="/" element={<Navigate to="overview" replace />} />
      </Routes>
    </Box>
  );
};

// Empty state when no brand is selected
const NoBrandSelected = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 400,
      textAlign: "center",
      p: 4,
    }}
  >
    <Typography
      variant="h6"
      sx={{
        color: "var(--intern-text-secondary)",
        mb: 1,
      }}
    >
      No Brand Selected
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "var(--intern-text-tertiary)",
        maxWidth: 400,
      }}
    >
      Select a brand from the sidebar to view its workspace. You can also browse
      all brands from the Platform section.
    </Typography>
  </Box>
);

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
      color: "var(--intern-text-tertiary)",
    }}
  >
    <CircularProgress size={24} color="inherit" />
  </Box>
);

// Main wrapper component
const InternBrandWorkspace = () => {
  const { selectedBrandId } = useBrandWorkspace();

  if (!selectedBrandId) {
    return <NoBrandSelected />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <WorkspaceContent brandId={selectedBrandId} />
    </Suspense>
  );
};

export default InternBrandWorkspace;
